'use client';
// release 63 (linkable assets): the copy-paste HTML of the "Listed on MatterCatalog" badge, linking to the brand page the visitor picks.
// The server render (and a browser without JavaScript) shows the code with "your-brand" in the address.
import { useState } from 'react';

export type BadgeBrand = { slug: string; name: string };

const escapeAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function badgeHtml(site: string, badgePath: string, brand: BadgeBrand | null): string {
  const slug = brand ? brand.slug : 'your-brand';
  const alt = brand ? `${brand.name} products listed on MatterCatalog` : 'Listed on MatterCatalog';
  return `<a href="${site}/matter-brands/${slug}"><img src="${site}${badgePath}" alt="${escapeAttribute(alt)}" width="180" height="48"></a>`;
}

export function BadgeCode({ brands, site, badgePath }: { brands: BadgeBrand[]; site: string; badgePath: string }) {
  const [slug, setSlug] = useState('');
  const [copied, setCopied] = useState(false);
  const brand = brands.find(b => b.slug === slug) || null;
  const html = badgeHtml(site, badgePath, brand);
  return (
    <div data-badge-code className="mt-3 max-w-3xl rounded-lg border border-slate-200 bg-white p-4">
      <label htmlFor="badge-brand" className="block text-sm font-semibold text-slate-900">Your brand</label>
      <select
        id="badge-brand"
        value={slug}
        onChange={event => { setSlug(event.target.value); setCopied(false); }}
        className="mt-1 min-h-11 w-full max-w-sm rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
      >
        <option value="">Choose your brand</option>
        {brands.map(b => <option key={b.slug} value={b.slug}>{b.name}</option>)}
      </select>
      <p className="mt-3 text-sm font-semibold text-slate-900">HTML to paste on your site</p>
      <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-800"><code data-badge-html>{html}</code></pre>
      {!brand && <p className="mt-1 text-xs text-slate-500">Choose your brand above, or replace <code>your-brand</code> with the last part of your brand page&apos;s address on MatterCatalog.</p>}
      <button
        type="button"
        onClick={() => { navigator.clipboard?.writeText(html).then(() => setCopied(true), () => setCopied(false)); }}
        className="mt-3 inline-flex min-h-11 items-center rounded-md border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 hover:bg-slate-50"
      >
        Copy HTML
      </button>
      <span role="status" aria-live="polite" className="ml-3 text-sm text-slate-600">{copied ? 'Copied.' : ''}</span>
    </div>
  );
}
